import { Box, Flex, Text } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SidebarLayout } from 'src/layouts/SidebarLayout';
import { RevisionSidebar } from '../SubmissionUpload/RevisionSidebar';
import { IRevisionRound } from '@publishing-engine/domain/models';
import StepHeader from '../SubmissionUpload/components/StepHeader';
import RevisionRequest from './RevisionRequest';
import { PreferencesTab } from 'src/components/Submission/MySubmission/PreferencesTab';
import { Button, useToast } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { SubmissionTab } from 'src/components/Submission/MySubmission/SubmissionTab';
import { AuthorsTab } from 'src/components/Submission/MySubmission/AuthorsTab';
import RevisedSubmission from './RevisedSubmission';
import { useNavigate } from 'react-router-dom';
import { useSubmitRevision } from 'src/api/commands';
import { useGenerateRevisionConsolidatedPdf } from 'src/api/mutations';
import { fetchRevisionConsolidatedPdfStatus } from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useWorkflow } from 'src/hooks/useWorkflow';
import { UnsubmitFeedbackAccordion } from 'src/components/Submission/MySubmission/components/feedback/UnsubmitFeedbackAccordion';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { getUnsubmittedReviewSections } from 'src/utils/unsubmittedReviewSections';
import { ResubmitModal } from 'src/components/Submission/decisionModals/ResubmitModal';
import type { ISubmissionFileSet } from '@publishing-engine/domain/models';
import ConsolidatedSubmissionFiles from '../SubmissionUpload/components/ConsolidatedSubmissionFiles';
import { getConsolidatedPdfErrorMessage } from '../SubmissionUpload/consolidatedPdfError';
import { getSourceFileSetSignature } from '../SubmissionUpload/fileSetSignature';
import {
  type ReplaceableFileField,
  savePendingFileReplacement
} from '../SubmissionUpload/fileReplacementNavigation';
import _ from 'lodash';

const getSubmissionFileSet = (
  fields: ReturnType<typeof useSubmissionData>['fieldsResponse']['fields'],
  valueType: 'value' | 'previousValue' = 'value'
): ISubmissionFileSet => {
  const fileSet: Record<string, unknown> = {};
  Object.entries(fields).forEach(([name, field]) => {
    const fieldPath = field.formFieldDefinition.caseDataFields?.main;
    if (typeof fieldPath === 'string' && fieldPath.startsWith('fileSet.')) {
      fileSet[fieldPath.slice('fileSet.'.length) || name] = field[valueType];
    }
  });
  return fileSet as ISubmissionFileSet;
};

const getSubmissionFileCategoryLabels = (
  fields: ReturnType<typeof useSubmissionData>['fieldsResponse']['fields']
) => {
  const labels: Record<string, string> = {};
  Object.entries(fields).forEach(([name, field]) => {
    const fieldPath = field.formFieldDefinition.caseDataFields?.main;
    const label = field.formFieldDefinition.params?.labels?.main;
    if (
      typeof fieldPath === 'string' &&
      fieldPath.startsWith('fileSet.') &&
      typeof label === 'string' &&
      label.trim()
    ) {
      labels[fieldPath.slice('fileSet.'.length) || name] = label.trim();
    }
  });
  return labels;
};

interface Props {
  currentStep: number;
  totalSteps: number;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  goToStep: (step: number) => void;
  onClose: () => void;
  revisionRound: IRevisionRound | undefined;
}

const ReviewRevisedSubmissionStep: React.FC<Props> = ({
  currentStep,
  goToStep,
  totalSteps,
  onClose,
  revisionRound,
  goToPreviousStep
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [isResubmitModalOpen, setIsResubmitModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consolidatedPdfStatus, setConsolidatedPdfStatus] = useState<
    'generating' | 'refreshing' | 'complete' | 'error' | null
  >(null);
  const [consolidatedPdfError, setConsolidatedPdfError] = useState<string | null>(
    null
  );
  const [hasCheckedConsolidatedPdfStatus, setHasCheckedConsolidatedPdfStatus] =
    useState(false);
  const [isConsolidatedPdfRequiredOpen, setIsConsolidatedPdfRequiredOpen] =
    useState(false);
  const [isAcknowledgementRequiredOpen, setIsAcknowledgementRequiredOpen] =
    useState(false);
  const [isConsolidatedPdfAcknowledged, setIsConsolidatedPdfAcknowledged] =
    useState(false);
  const [openPreviewForAcknowledgement, setOpenPreviewForAcknowledgement] =
    useState(false);
  const {
    workflowResponse: {
      submissionId,
      submissionContextId,
      workflowData: { currentState }
    },
    organizationId,
    refetchSubmission,
    activeRevision,
    fieldsResponse,
    submissionContext
  } = useSubmissionData();
  const [consolidatedFileSet, setConsolidatedFileSet] = useState(() =>
    getSubmissionFileSet(fieldsResponse.fields)
  );
  const appliedInitialStatusForSubmission = useRef<string | null>(null);
  const fileCategoryLabels = getSubmissionFileCategoryLabels(
    fieldsResponse.fields
  );
  const enableConsolidatedPdf =
    submissionContext?.config?.enableConsolidatedPdf;
  const generateConsolidatedPdfMutation = useGenerateRevisionConsolidatedPdf();
  const initialConsolidatedPdfStatusQuery = useQuery({
    queryKey: [
      'revision-consolidated-pdf-status',
      submissionContextId,
      submissionId
    ],
    queryFn: () =>
      fetchRevisionConsolidatedPdfStatus(submissionContextId, submissionId),
    enabled: Boolean(
      enableConsolidatedPdf && submissionContextId && submissionId
    ),
    staleTime: 0,
    cacheTime: Infinity,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  const {
    data: initialConsolidatedPdfStatus,
    isFetched: hasFetchedInitialConsolidatedPdfStatus,
    isFetchedAfterMount: fetchedInitialConsolidatedPdfStatusAfterMount,
    isError: failedInitialConsolidatedPdfStatus
  } = initialConsolidatedPdfStatusQuery;

  useEffect(() => {
    setConsolidatedFileSet(getSubmissionFileSet(fieldsResponse.fields));
  }, [fieldsResponse.fields]);

  useEffect(() => {
    if (!enableConsolidatedPdf) return;

    if (hasFetchedInitialConsolidatedPdfStatus) {
      setHasCheckedConsolidatedPdfStatus(true);
    }

    // Apply the initial result only when this mount made the request. A cached
    // `not-started` result must not clear files after a later PDF generation.
    const submissionKey = `${submissionContextId}:${submissionId}`;
    if (
      !fetchedInitialConsolidatedPdfStatusAfterMount ||
      appliedInitialStatusForSubmission.current === submissionKey
    ) return;
    appliedInitialStatusForSubmission.current = submissionKey;

    if (failedInitialConsolidatedPdfStatus) {
      toast({
        variant: 'error',
        title: 'Unable to check consolidated PDF status',
        description: 'Please try again.',
        isClosable: true
      });
      return;
    }

    if (initialConsolidatedPdfStatus?.status === 'not-started') {
      setConsolidatedFileSet(previous => {
        const sourceFiles = { ...previous };
        delete sourceFiles.ConsolidatedPdf;
        delete sourceFiles.AnonymizedConsolidatedPdf;
        return sourceFiles as ISubmissionFileSet;
      });
    } else if (initialConsolidatedPdfStatus?.status === 'completed') {
      if (
        initialConsolidatedPdfStatus.file &&
        initialConsolidatedPdfStatus.anonymizedFile
      ) {
        setConsolidatedFileSet(previous => ({
          ...previous,
          ConsolidatedPdf: initialConsolidatedPdfStatus.file,
          AnonymizedConsolidatedPdf:
            initialConsolidatedPdfStatus.anonymizedFile
        }));
      }
    }
  }, [
    enableConsolidatedPdf,
    submissionContextId,
    submissionId,
    hasFetchedInitialConsolidatedPdfStatus,
    fetchedInitialConsolidatedPdfStatusAfterMount,
    initialConsolidatedPdfStatus,
    failedInitialConsolidatedPdfStatus,
    toast
  ]);

  const { articleWorkflow } = useWorkflow();

  const submitRevisionMutation = useSubmitRevision({
    submissionId,
    submissionContextId,
    organizationId,
    articleWorkflowId: articleWorkflow.id,
    invalidateSubmissionQueries: refetchSubmission
  });
  const isUnsubmitted = currentState === 'unsubmitted';
  const { checkFormFieldValueIsUpdated } = useSubmissionFields('submission');
  const unsubmittedReviewSections = getUnsubmittedReviewSections(
    checkFormFieldValueIsUpdated
  );
  const isConsolidatedPdfGenerationComplete =
    consolidatedPdfStatus === 'complete';
  const isFileUpdated =
    !isConsolidatedPdfGenerationComplete &&
    getSourceFileSetSignature(getSubmissionFileSet(fieldsResponse.fields)) !==
      getSourceFileSetSignature(
        getSubmissionFileSet(fieldsResponse.fields, 'previousValue')
      );
  const isResponseUpdated = [
    'AuthorResponse',
    'AuthorResponseAttachments'
  ].some(fieldName => {
    const field = fieldsResponse.fields[fieldName];
    return (
      field?.previousValue !== undefined &&
      !_.isEqual(field.value, field.previousValue)
    );
  });
  const responseUpdateFlag = (
    fieldsResponse as typeof fieldsResponse & { isUpdated?: boolean }
  ).isUpdated;
  const hasFileChanges =
    !isConsolidatedPdfGenerationComplete &&
    (isFileUpdated ||
      (fieldsResponse as typeof fieldsResponse & { isFileUpdated?: boolean })
        .isFileUpdated === true ||
      responseUpdateFlag === true ||
      isResponseUpdated);

  const handleReplaceFile = (field: ReplaceableFileField) => {
    const isManuscriptOrTitlePage =
      field.key === 'Manuscript' || field.key === 'TitlePage';
    const stepIndex = isUnsubmitted
      ? field.stepKey === 'author'
        ? 1
        : field.stepKey === 'preference'
          ? 2
          : 0
      : isManuscriptOrTitlePage
        ? 0
        : field.stepKey === 'author'
          ? 2
          : field.stepKey === 'preference'
            ? 3
            : 1;
    savePendingFileReplacement(field);
    goToStep(stepIndex);
  };

  const generateConsolidatedPdf = async (rerun = false) => {
    setConsolidatedPdfError(null);
    if (rerun) setIsConsolidatedPdfAcknowledged(false);
    setConsolidatedPdfStatus('generating');

    try {
      const result = await generateConsolidatedPdfMutation.mutateAsync({
        submissionContextId,
        submissionId,
        rerun
      });
      const generationRequestWasTooLarge =
        !result.isSuccess && 'status' in result && result.status === 413;

      if (!result.isSuccess && !generationRequestWasTooLarge) {
        const errorMessage = getConsolidatedPdfErrorMessage(result.message);
        setConsolidatedPdfStatus('error');
        setConsolidatedPdfError(errorMessage || 'Please try again.');
        toast({
          variant: 'error',
          title: 'Consolidated PDF generation failed',
          description: errorMessage || 'Please try again.',
          isClosable: true
        });
        return;
      }

      setConsolidatedPdfStatus('refreshing');
      if (generationRequestWasTooLarge) {
        await new Promise(resolve => setTimeout(resolve, 5000));
      }

      let status = await fetchRevisionConsolidatedPdfStatus(
        submissionContextId,
        submissionId
      );
      while (status.status !== 'completed') {
        if (status.status === 'failed') {
          setConsolidatedPdfStatus('error');
          setConsolidatedPdfError(status.error || 'Please try again.');
          toast({
            variant: 'error',
            title: 'Consolidated PDF generation failed',
            description: status.error || 'Please try again.',
            isClosable: true
          });
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 5000));
        status = await fetchRevisionConsolidatedPdfStatus(
          submissionContextId,
          submissionId
        );
      }

      const consolidatedPdf = status.file;
      const anonymizedConsolidatedPdf = status.anonymizedFile;
      if (consolidatedPdf && anonymizedConsolidatedPdf) {
        setConsolidatedFileSet(previous => ({
          ...previous,
          ConsolidatedPdf: consolidatedPdf,
          AnonymizedConsolidatedPdf: anonymizedConsolidatedPdf
        }));
        refetchSubmission();
      }
      setConsolidatedPdfStatus('complete');
      toast({
        variant: 'success',
        title: 'Consolidated PDF generated',
        description: 'The consolidated PDF files are ready.',
        isClosable: true
      });
    } catch (error) {
      const errorMessage = getConsolidatedPdfErrorMessage(error);
      setConsolidatedPdfStatus('error');
      setConsolidatedPdfError(errorMessage || 'Please try again.');
      toast({
        variant: 'error',
        title: 'Consolidated PDF generation failed',
        description: errorMessage || 'Please try again.',
        isClosable: true
      });
    }
  };

  const handleSubmitClick = () => {
    if (enableConsolidatedPdf) {
      if (!hasCheckedConsolidatedPdfStatus) return;
      if (hasFileChanges) {
        setIsConsolidatedPdfRequiredOpen(true);
        return;
      }
      if (
        !consolidatedFileSet.ConsolidatedPdf ||
        !consolidatedFileSet.AnonymizedConsolidatedPdf
      ) {
        setIsConsolidatedPdfRequiredOpen(true);
        return;
      }
      if (!isConsolidatedPdfAcknowledged) {
        setIsAcknowledgementRequiredOpen(true);
        return;
      }
    }

    if (isUnsubmitted) {
      setIsResubmitModalOpen(true);
    } else {
      void onSubmit();
    }
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const res = await submitRevisionMutation.mutateAsync({
        authorResponse: activeRevision?.authorResponse || '', // sending author response so backend can do the validation for it
        isFileUpdated,
        isUpdated: isResponseUpdated || responseUpdateFlag === true
      });

      if (res.isSuccess) {
        toast({
          variant: 'success',
          title: 'Revision Submitted',
          description: 'The editors will review your submission.',
          isClosable: true
        });
        navigate('?submissionType=my-submissions-active');
      } else {
        toast({
          variant: 'error',
          title: 'Error submitting revision',
          description:
            res.message?.errors
              ?.map((err: { reason: string }) => err.reason)
              .join(', ') || 'Missing or invalid data'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      {isUnsubmitted && isResubmitModalOpen && (
        <ResubmitModal
          onClose={() => setIsResubmitModalOpen(false)}
          submissionId={submissionId}
          submissionContextId={submissionContextId}
          organizationId={organizationId}
          isFileUpdated={isFileUpdated}
          isUpdated={isResponseUpdated || responseUpdateFlag === true}
        />
      )}
      <Flex
        flexDirection="column"
        backgroundColor="surfaceSecondaryFill"
        minH="100vh"
      >
        <SidebarLayout
          sidebar={
            <RevisionSidebar
              currentStep={currentStep}
              onNavigate={step => {
                goToStep(step);
              }}
            />
          }
        >
          <StepHeader
            currentStep={currentStep + 1}
            totalSteps={totalSteps}
            heading={
              isUnsubmitted ? 'Review Submission' : 'Review author information'
            }
            description={
              isUnsubmitted
                ? 'Please review all the details of your submission carefully. Ensure that every piece of information is accurate and complete before proceeding. Once you are confident that everything is correct, you may proceed to resubmit your work.'
                : 'Review and edit the extracted author information to ensure all required information is included.'
            }
          />
          <UnsubmitFeedbackAccordion />
          {revisionRound && <RevisionRequest revisionRound={revisionRound} />}
          <Flex flexDirection="column" gap={2}>
            {!isUnsubmitted && (
              <Flex flexDirection="column" gap={2}>
                <RevisedSubmission />
              </Flex>
            )}
            <Flex flexDirection="column" gap={2}>
              {isUnsubmitted ? (
                <>
                  {(unsubmittedReviewSections.hasSubmissionInformationUpdates ||
                    unsubmittedReviewSections.hasSubmissionFileUpdates) && (
                    <Box id="Submission information">
                      {unsubmittedReviewSections.hasSubmissionInformationUpdates && (
                        <Text textStyle="headingGroup" mb={3}>
                          Submission information
                        </Text>
                      )}
                      <SubmissionTab
                        isUpdateRequired={false}
                        isRevisionReview
                      />
                    </Box>
                  )}
                  {unsubmittedReviewSections.hasAuthorInformationUpdates && (
                    <Box id="Author information">
                      <Text textStyle="headingGroup" mb={3}>
                        Author information
                      </Text>
                      <AuthorsTab
                        showTitle={false}
                        isRevisionReview
                        isUpdateRequired={false}
                      />
                    </Box>
                  )}
                  {unsubmittedReviewSections.hasSubmissionPreferenceUpdates && (
                    <Box id="Submission preferences">
                      <Text textStyle="headingGroup" mb={3}>
                        Submission preferences
                      </Text>
                      <PreferencesTab
                        showTitle={false}
                        isRevisionReview
                        isUpdateRequired={false}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <SubmissionTab isUpdateRequired={false} isRevisionReview />
                  <AuthorsTab
                    showTitle={false}
                    isRevisionReview
                    isUpdateRequired={false}
                  />
                  <PreferencesTab
                    showTitle={false}
                    isRevisionReview
                    isUpdateRequired={false}
                  />
                </>
              )}
            </Flex>
          </Flex>
          {enableConsolidatedPdf && hasCheckedConsolidatedPdfStatus && (
            <ConsolidatedSubmissionFiles
              fileSet={consolidatedFileSet}
              hasFileChanges={hasFileChanges}
              categoryLabels={fileCategoryLabels}
              onReplaceFile={handleReplaceFile}
              onGenerate={generateConsolidatedPdf}
              isGenerating={
                consolidatedPdfStatus === 'generating' ||
                consolidatedPdfStatus === 'refreshing'
              }
              generationStatus={consolidatedPdfStatus}
              generationError={consolidatedPdfError}
              onCheckStatus={() => setConsolidatedPdfStatus(null)}
              submissionContextId={submissionContextId}
              organizationId={organizationId}
              isRequiredModalOpen={isConsolidatedPdfRequiredOpen}
              onCloseRequiredModal={() =>
                setIsConsolidatedPdfRequiredOpen(false)
              }
              isAcknowledgementRequiredModalOpen={isAcknowledgementRequiredOpen}
              onCloseAcknowledgementRequiredModal={() =>
                setIsAcknowledgementRequiredOpen(false)
              }
              onProceedToAcknowledgement={() => {
                setIsAcknowledgementRequiredOpen(false);
                setOpenPreviewForAcknowledgement(true);
              }}
              isAcknowledged={isConsolidatedPdfAcknowledged}
              onAcknowledge={() => setIsConsolidatedPdfAcknowledged(true)}
              openPreviewForAcknowledgement={openPreviewForAcknowledgement}
              onPreviewOpenedForAcknowledgement={() =>
                setOpenPreviewForAcknowledgement(false)
              }
            />
          )}
        </SidebarLayout>
      </Flex>
      <Flex
        padding={4}
        bottom={0}
        zIndex={1}
        justifyContent="center"
        position="sticky"
        bgColor="surfacePrimaryFill"
        marginX="auto"
      >
        <Flex width="100%" justifyContent="space-between">
          {/* ----- Footer Buttons ----- */}
          <Button
            variant="caution"
            onClick={() => {
              onClose();
            }}
          >
            Exit
          </Button>
          <Flex gap={4}>
            <Button
              variant="secondary"
              leftIcon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={goToPreviousStep}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitClick}
              isLoading={isSubmitting}
              disabled={
                isSubmitting ||
                (enableConsolidatedPdf && !hasCheckedConsolidatedPdfStatus)
              }
            >
              Submit
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default ReviewRevisedSubmissionStep;
